import React, { useState, useEffect } from "react";
import { useParams, Link, Navigate, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { findCharacter } from "../data/characters1";
import { Atmosphere } from "../components/Atmosphere";
import { 
  ChevronLeft, 
  Music, 
  Zap, 
  Shield, 
  Crown, 
  Sparkles 
} from "lucide-react";


export default function CharacterProfile() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const c = findCharacter(slug);

  // 1. Sprache aus dem Speicher holen & Event-Lauscher für die Navbar aufsetzen
  const [currentLang, setCurrentLang] = useState(() => {
    return localStorage.getItem("project_lang") || "de";
  });

  useEffect(() => {
    const handleLangChange = () => {
      setCurrentLang(localStorage.getItem("project_lang") || "de");
    };
    window.addEventListener("languageChange", handleLangChange);
    return () => window.removeEventListener("languageChange", handleLangChange);
  }, []);

  if (!c) return <Navigate to="/charaktere" replace />;

  // Sichere Auflösung der übersetzten Werte für die 4 neuen Kategorien
  const currentPower = c.power?.[currentLang] || c.power?.["de"];
  const currentEye = c.eye?.[currentLang] || c.eye?.["de"];
  const currentQuote = c.quote?.[currentLang] || c.quote?.["de"];

  // Korrigierte Keys aus den Charakterdaten:
  const currentSpecialty = c.specialty?.[currentLang] || c.specialty?.["de"];
  const currentWeakness = c.weakness?.[currentLang] || c.weakness?.["de"];
  const currentRank = c.rank?.[currentLang] || c.rank?.["de"];
  const currentSecretWish = c.secretWish?.[currentLang] || c.secretWish?.["de"];

  return (
    <div className="pt-28 pb-24 relative" data-testid={`profile-${c.slug}`}>
      <Atmosphere variant="minimal" />

      {/* Banner */}
      <div className="relative h-[70vh] overflow-hidden">
        {/* Accent backdrop */}
        <div
          className="absolute inset-0"
          style={{
            background: `radial-gradient(circle at 50% 40%, ${c.accent}30 0%, #080808 70%)`,
          }}
        />
        {/* Hero portrait, blurred + scaled as ambient backdrop */}
        <img
          src={`/characters/${c.slug}.jpg`}
          alt=""
          aria-hidden
          className="absolute inset-0 w-full h-full object-cover opacity-25"
          style={{ filter: "blur(24px) saturate(0.7) brightness(0.7)" }}
        />
        {/* Foreground portrait */}
        <div className="absolute inset-0 flex items-end justify-center pt-10">
          <div className="relative h-[85%] aspect-[3/4]">
            <img
              src={`/characters/${c.slug}.jpg`}
              alt={c.name}
              className="h-full w-full object-cover"
              style={{ filter: "saturate(0.9) contrast(1.05) brightness(0.95)" }}
            />
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                background: `linear-gradient(to bottom, transparent 50%, #080808 100%), linear-gradient(to right, ${c.accent}11, transparent 30%, transparent 70%, ${c.accent}11)`,
              }}
            />
            <div className="corner-orn tl" style={{ borderColor: c.accent }} />
            <div className="corner-orn tr" style={{ borderColor: c.accent }} />
            <div className="corner-orn bl" style={{ borderColor: c.accent }} />
            <div className="corner-orn br" style={{ borderColor: c.accent }} />
          </div>
        </div>
        {/* Chinese character watermark overlay */}
        <span
          className="chinese absolute left-1/2 top-[28%] -translate-x-1/2 text-[14rem] opacity-10 pointer-events-none"
          style={{ color: c.accent }}
        >
          {c.chinese?.charAt(0)}
        </span>
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-[#080808] to-transparent h-48" />
      </div>

      <div className="max-w-5xl mx-auto px-6 -mt-40 relative z-10">
        <button 
          onClick={() => navigate(-1)} 
          className="inline-flex items-center gap-2 text-[#C9A227] mb-8 hover:text-[#E8DDC8] transition-colors cursor-pointer"
        >
          <ChevronLeft size={16} /> 
          <span className="text-xs tracking-[0.25em] uppercase">
            {currentLang === "en" ? "Back to all characters" : "Zu allen Charakteren"}
          </span>
        </button>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
          className="text-center mb-16"
        >
          <p className="chinese text-5xl mb-3" style={{ color: c.accent }}>{c.chinese}</p>
          <h1 className="serif text-6xl lg:text-8xl text-[#E8DDC8] tracking-tight">{c.name}</h1>
          <p className="text-xs tracking-[0.35em] uppercase text-[#7A7C7D] mt-3">
            {c.age} · {c.kingdom}
          </p>

          {/* Bedeutung des Namens (Quote) in Anführungszeichen */}
          {currentQuote && (
            <p className="serif italic text-2xl text-[#B3B6B7] mt-8 max-w-2xl mx-auto">
              „{currentQuote}“
            </p>
          )}

          <span
            className="inline-block mt-6 text-xs tracking-[0.3em] uppercase px-3 py-1 border"
            style={{ borderColor: c.accent, color: c.accent }}
          >
            {c.tag}
          </span>
        </motion.div>

        {/* Die 3 Hauptkategorien (Kraft, Augenfarbe, Königreich) */}
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          {[
            { label: currentLang === "en" ? "Power" : "Kraft", value: currentPower },
            { label: currentLang === "en" ? "Eye Color" : "Augenfarbe", value: currentEye },
            { label: currentLang === "en" ? "Kingdom" : "Königreich", value: c.kingdom },
          ].map((s) => (
            <div key={s.label} className="card-imperial p-6 text-center" data-testid={`stat-${s.label}`}>
              <p className="text-xs tracking-[0.3em] uppercase text-[#C9A227] mb-2">{s.label}</p>
              <p className="serif text-2xl text-[#E8DDC8]">{s.value}</p>
            </div>
          ))}
        </div>

        {/* Energy bar */}
        <div className="card-imperial p-8 mb-6">
          <div className="flex justify-between items-baseline mb-3">
            <p className="text-xs tracking-[0.3em] uppercase text-[#C9A227]">
              {currentLang === "en" ? "Energy Level" : "Energielevel"}
            </p>
            <p className="serif text-2xl" style={{ color: c.accent }}>{c.energy} / {c.energyMax}</p>
          </div>
          <div className="h-2 bg-[#1a1a1a] relative overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${(c.energy / c.energyMax) * 100}%` }}
              transition={{ duration: 1.5, ease: "easeOut" }}
              className="h-full"
              style={{ background: `linear-gradient(to right, ${c.accent}80, ${c.accent})` }}
            />
          </div>
        </div>

        {/* 1. Höfischer Status / Rang */}
        {currentRank && (
          <div className="card-imperial p-6 mb-6">
            <div className="flex items-center gap-3 mb-3">
              <Crown size={18} style={{ color: c.accent }} />
              <p className="text-xs tracking-[0.3em] uppercase text-[#C9A227]">
                {currentLang === "en" ? "Court Status & Rank" : "Höfischer Status & Rang"}
              </p>
            </div>
            <p className="serif text-xl text-[#E8DDC8] italic leading-relaxed">{currentRank}</p>
          </div>
        )}

        {/* 2. Spezialität / Meisterschaft & 3. Schwäche / Achillesferse */}
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          <div className="card-imperial p-6">
            <div className="flex items-center gap-3 mb-3">
              <Zap size={18} style={{ color: c.accent }} />
              <p className="text-xs tracking-[0.3em] uppercase text-[#C9A227]">
                {currentLang === "en" ? "Specialty & Mastery" : "Spezialität / Meisterschaft"}
              </p>
            </div>
            <p className="serif text-xl text-[#E8DDC8] italic leading-relaxed">{currentSpecialty}</p>
          </div>

          <div className="card-imperial p-6">
            <div className="flex items-center gap-3 mb-3">
              <Shield size={18} style={{ color: c.accent }} />
              <p className="text-xs tracking-[0.3em] uppercase text-[#C9A227]">
                {currentLang === "en" ? "Weakness / Achilles Heel" : "Schwäche / Achillesferse"}
              </p>
            </div>
            <p className="serif text-xl text-[#E8DDC8] italic leading-relaxed">{currentWeakness}</p>
          </div>
        </div>

        {/* 4. Heimlicher Wunsch */}
        <div className="card-imperial p-6 mb-6">
          <div className="flex items-center gap-3 mb-3">
            <Sparkles size={18} style={{ color: c.accent }} />
            <p className="text-xs tracking-[0.3em] uppercase text-[#C9A227]">
              {currentLang === "en" ? "Secret Wish" : "Heimlicher Wunsch"}
            </p>
          </div>
          <p className="serif text-xl text-[#E8DDC8] italic leading-relaxed">
            {currentSecretWish || "—"}
          </p>
        </div>

        {/* Song */}
        {c.song && (
          <div className="card-imperial p-8 mb-12 flex items-center gap-4">
            <Music size={22} style={{ color: c.accent }} />
            <div>
              <p className="text-xs tracking-[0.3em] uppercase text-[#C9A227] mb-1">
                {currentLang === "en" ? "Song of the Soul" : "Lied der Seele"}
              </p>
              <p className="serif text-lg text-[#E8DDC8] italic">{c.song}</p>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}